import type { RestaurantPublicDTO, TableRefDTO } from '@scan2eat/shared';
import { DISPLAY_CONTEXT_STORAGE_KEY } from '@scan2eat/shared';

import { readStoredValue } from './storage.js';

/**
 * Contexte d'affichage du client : nom du restaurant et libelle de table,
 * recupere a la resolution du QR code puis conserve pour les en-tetes.
 * Cle locale a web-client : le paquet shared n'expose pas cette cle.
 */

export interface DisplayContext {
  restaurant: RestaurantPublicDTO;
  table: Pick<TableRefDTO, 'label' | 'zone'>;
}

export function saveDisplayContext(ctx: DisplayContext): void {
  try {
    localStorage.setItem(DISPLAY_CONTEXT_STORAGE_KEY, JSON.stringify(ctx));
  } catch {
    /* noop */
  }
  applyBrandPalette(ctx.restaurant);
  applyFavicon(ctx.restaurant.logoUrl);
}

/** Adresse par defaut, celle deja posee dans index.html. */
const DEFAULT_FAVICON = '/favicon.svg';

/**
 * Remplace l'icone d'onglet par le logo du restaurant. Sans logo, retombe
 * sur l'icone Scan2Eat par defaut -- jamais d'onglet sans icone.
 *
 * Un navigateur qui visite plusieurs restaurants dans des onglets distincts
 * ne les confond pas : chaque onglet lit et applique ses propres donnees,
 * il n'y a pas d'etat partage entre eux.
 */
export function applyFavicon(logoUrl: string | null): void {
  // Effet de bord de navigateur : `saveDisplayContext` est aussi appelee la ou
  // il n'y a pas de DOM, et ranger le contexte ne doit pas en dependre.
  if (typeof document === 'undefined') return;

  let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    document.head.appendChild(link);
  }
  link.href = logoUrl ?? DEFAULT_FAVICON;
}

export function getDisplayContext(): DisplayContext | null {
  try {
    const raw = readStoredValue(DISPLAY_CONTEXT_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as DisplayContext) : null;
  } catch {
    return null;
  }
}

/** Couleurs de marque de tokens.css que la palette peut surcharger. */
const BRAND_CSS_VARIABLES = [
  '--brand-espresso',
  '--brand-cacao',
  '--brand-creme',
  '--brand-blanc-casse',
  '--brand-sable',
] as const;

/**
 * Applique la palette de marque du restaurant, en surchargeant les couleurs
 * de marque de tokens.css -- pas les variables semantiques (`--color-*`),
 * qui en derivent par `var()`. Plusieurs composants, dont l'en-tete du menu
 * (`.hero` de MenuPage), utilisent une couleur de marque directement
 * (`var(--brand-espresso)`) sans passer par la couche semantique ; surcharger
 * seulement `--color-primary` et consorts les laissait donc inchanges.
 * Comme la resolution d'une variable CSS est dynamique, surcharger la source
 * suffit a propager le changement partout ou elle est utilisee.
 *
 * **Retire aussi la surcharge quand le restaurant n'a pas de palette.**
 * Un meme navigateur qui visite le restaurant A (avec palette) puis le
 * restaurant B (sans palette) gardait sinon les couleurs de A affichees sur
 * le site de B -- une fuite d'un restaurant a l'autre dans le meme appareil,
 * jamais entre deux appareils differents (chaque navigateur applique sa
 * propre palette a partir de ses propres donnees, jamais un etat partage).
 *
 * Voir docs/adr/0025-palette-de-marque-proposee-par-une-ia.md.
 */
export function applyBrandPalette(restaurant: RestaurantPublicDTO): void {
  // Meme raison qu'au-dessus : sans DOM, il n'y a pas de palette a poser.
  if (typeof document === 'undefined') return;

  const { brandPrimaryColor, brandBackgroundColor, brandTextColor, brandAccentColor } = restaurant;
  const root = document.documentElement.style;

  if (!brandPrimaryColor || !brandBackgroundColor || !brandTextColor || !brandAccentColor) {
    for (const variable of BRAND_CSS_VARIABLES) root.removeProperty(variable);
    return;
  }

  // Texte principal, et fonds immersifs comme l'en-tete du menu.
  root.setProperty('--brand-espresso', brandTextColor);
  // Couleur d'action : boutons, liens, etats actifs.
  root.setProperty('--brand-cacao', brandPrimaryColor);
  // Fond de l'application, et des cartes.
  root.setProperty('--brand-creme', brandBackgroundColor);
  root.setProperty('--brand-blanc-casse', brandBackgroundColor);
  // Bordures, boutons secondaires, et texte pose sur un fond immersif
  // (l'en-tete du menu lit sa couleur de texte directement sur --brand-sable).
  root.setProperty('--brand-sable', brandAccentColor);
}
